import { useCallback, useEffect, useMemo, useRef } from 'react';

/*
 * Custom hook for infinite scroll using Intersection Observer API
 * @param {Function} loadMore - Function to load more data
 * @param {Boolean} hasMore - Whether there is more data to load
 * @param {Boolean} isLoading - Whether the data is currently being loaded
 * @param {Object} options - Intersection Observer options
 * @returns {Object} - Object containing the loadingRef, isLoading, and hasMore
 */
export const useInfiniteScroll = ({
    loadMore,
    hasMore = true,
    isLoading = false,
    options = {},
}) => {
    const loadingRef = useRef(null);
    const observerRef = useRef(null);
    const scrollHandlerRef = useRef(null);
    const setupTimeoutRef = useRef(null);

    const defaultOptions = useMemo(
        () => ({
            root: null,
            rootMargin: '20px',
            threshold: 0.1,
            ...options,
        }),
        [options]
    );

    const handleObserver = useCallback(
        (entries) => {
            const [entry] = entries;
            if (entry.isIntersecting && hasMore && !isLoading) {
                loadMore();
            }
        },
        [hasMore, isLoading, loadMore]
    );

    const handleScrollFallback = useCallback(() => {
        const element = loadingRef.current;
        if (!element || !hasMore || isLoading) return;

        const rect = element.getBoundingClientRect();
        const isVisible = rect.top <= window.innerHeight + 30;

        if (isVisible) {
            loadMore();
        }
    }, [hasMore, isLoading, loadMore]);

    const cleanupObserver = () => {
        observerRef.current?.disconnect();
        observerRef.current = null;
    };

    const cleanupFallbackScroll = () => {
        if (scrollHandlerRef.current) {
            window.removeEventListener('scroll', scrollHandlerRef.current);
            scrollHandlerRef.current = null;
        }
    };

    const setupObserver = useCallback(() => {
        cleanupObserver();
        cleanupFallbackScroll();

        const element = loadingRef.current;
        if (!element || !(element instanceof Element)) return;

        if ('IntersectionObserver' in window) {
            observerRef.current = new IntersectionObserver(handleObserver, defaultOptions);
            observerRef.current.observe(element);
        } else {
            scrollHandlerRef.current = handleScrollFallback;
            window.addEventListener('scroll', scrollHandlerRef.current, { passive: true });
        }
    }, [defaultOptions, handleObserver, handleScrollFallback]);

    useEffect(() => {
        if (setupTimeoutRef.current) {
            clearTimeout(setupTimeoutRef.current);
        }

        if (hasMore && !isLoading) {
            setupTimeoutRef.current = setTimeout(setupObserver, 100);
        }

        return () => {
            if (setupTimeoutRef.current) {
                clearTimeout(setupTimeoutRef.current);
            }
            cleanupObserver();
            cleanupFallbackScroll();
        };
    }, [setupObserver, hasMore, isLoading]);

    return { loadingRef };
};
